import React, { useContext } from "react";
import BottomBar from "components/bottom-bar";
import SearchBar from "components/search-tradeins";
import { useGetTakebacks } from "api/tes/useGetTakebacks";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AuthContext from "contexts/AuthContext";
import { ProfileHead } from "pages/svgs/profile";

export default function TradeIns() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [search, setSearch] = React.useState("");

  const storeState = `in_store_${user?.state?.toLowerCase() || "co"}`;

  // Fetch takebacks filtered by store using GraphQL with better caching
  const {
    data: takebacksData,
    isLoading,
    error,
  } = useGetTakebacks(storeState, {
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch on component remount if data exists
    keepPreviousData: true, // Keep previous data while refetching to prevent flashing
  });
  const searchInObject = (obj, searchTerm) => {
    if (obj === null || obj === undefined) return false;

    if (typeof obj !== "object") {
      return String(obj).toLowerCase().includes(searchTerm.toLowerCase());
    }

    // Search in all object values recursively
    return Object.values(obj).some((value) =>
      searchInObject(value, searchTerm)
    );
  };

  const takebacks = search
    ? takebacksData?.data?.takebacks?.items.filter((item) =>
        searchInObject(item, search)
      )
    : takebacksData?.data?.takebacks?.items || [];

  // Simple list item for takebacks
  const TakebackListItem = ({ takeback, idx }) => {
    const data = takeback.data || {};

    const displayName = takeback.customer_id_belongsTo
      ? `${takeback.customer_id_belongsTo.name} ${takeback.customer_id_belongsTo.last_name}`
      : `Trade-in ${takeback.id.slice(-6)}`;
    const weight = data.originalEstimatedWeight || takeback.quantity || "";
    const completed = !!data.legoValue && data.legoValue !== "0";

    return (
      <Link to={`${t("urls.store_trade_ins")}/${takeback.id}`}>
        <div className="bg-white px-4 py-4 rounded-lg border-gray-300 border cursor-pointer hover:bg-gray-50">
          <div className="flex items-start">
            <div
              className={`mr-4 my-auto rounded-full px-2 py-1.5 ${
                completed
                  ? "bg-[#C0DFC3] border-green border-2"
                  : "bg-[#E4E4E4]"
              }`}
            >
              <ProfileHead />
            </div>
            <div className="flex-1">
              <div className="font-medium text-[#000000]">{displayName}</div>
              <div className="text-[#2C2C2C] font-light">
                {Number(weight).toFixed(2)}
                {t("scale.weight")} of {idx === 0 ? `LEGO®` : `LEGO`} bricks
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  // Skeleton card component for loading state
  const SkeletonCard = () => (
    <div className="bg-white px-4 py-4 rounded-lg border-gray-300 border animate-pulse">
      <div className="flex items-start">
        <div className="mr-4 my-auto rounded-full px-2 py-1.5 bg-gray-200 w-12 h-12"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    </div>
  );

  // Show skeleton loading state
  if (isLoading) {
    return (
      <div className="flex flex-col px-6 pt-4 pb-28 items-center min-h-full">
        <div className="flex flex-col mr-auto">
          <div>
            <h1 className="text-6xl pb-2 font-bold">
              {t("store.trade-ins.title")}
            </h1>
          </div>
        </div>
        <div className="flex flex-col items-left justify-between w-full">
          <span className="text-regular text-primary">
            {t("store.trade-ins.search")}
          </span>
          <SearchBar
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled
          />
        </div>
        <div className="grid w-full gap-large">
          {[...Array(6)].map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
        <BottomBar />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl text-red-600">Error loading trade-ins</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-6 pt-4 pb-28 items-center min-h-full">
      <div className="flex flex-col mr-auto">
        <div>
          <h1 className="text-6xl pb-2 font-bold">
            {t("store.trade-ins.title")}
          </h1>
        </div>
      </div>
      <div className="flex flex-col items-left justify-between w-full">
        <span className="text-regular text-primary">
          {t("store.trade-ins.search")}
        </span>
        <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="grid w-full gap-large">
        {takebacks.map((takeback, index) => (
          <TakebackListItem
            idx={index}
            key={takeback.id || index}
            takeback={takeback}
          />
        ))}
      </div>
      <BottomBar />
    </div>
  );
}
